import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Quote, ArrowUpRight } from "lucide-react";
import { IMG, SERVICES, STATS, TESTIMONIALS, PARTNERS, COMPANY } from "../data/site";
import { Reveal, MaskHeading, Stagger, StaggerItem } from "../components/site/motion";
import { Overline, Btn, StatCounter, Section, IconTile } from "../components/site/bits";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-brand-main pt-36 pb-16 md:pt-44 md:pb-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <Reveal><Overline>Supported Exempt Accommodation · Coventry</Overline></Reveal>
          <h1 className="mt-6 font-serif text-[16vw] leading-[0.86] tracking-tight text-brand-dark sm:text-7xl lg:text-8xl">
            <MaskHeading lines={["Safe Housing.", <span key="s" className="italic text-brand-green">Strong Futures.</span>]} delay={0.15} />
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-8 max-w-xl text-lg text-brand-slate">
              We provide safe accommodation and personalised support to refugees and vulnerable adults — helping people rebuild their lives with dignity, confidence and independence.
            </p>
          </Reveal>
          <Reveal delay={0.7}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Btn to="/referrals" testid="hero-referral-btn">Make a Referral</Btn>
              <Btn to="/support-services" variant="outline" testid="hero-services-btn">Explore Support</Btn>
            </div>
          </Reveal>
        </div>
        <div className="relative lg:col-span-5">
          <motion.div style={{ y: y1 }} className="arch-frame relative aspect-[3/4] w-full max-w-sm border border-brand-dark/10">
            <img src={IMG.heroMan} alt="A resident supported by Shelterease" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/30 to-transparent" />
          </motion.div>
          <motion.div style={{ y: y2 }} className="absolute -bottom-8 -left-6 hidden aspect-square w-40 overflow-hidden rounded-2xl border-4 border-brand-main shadow-xl sm:block lg:-left-16 lg:w-48">
            <img src={IMG.houseInterior} alt="Modern shared accommodation at Shelterease" className="h-full w-full object-cover" />
          </motion.div>
          <motion.div style={{ y: y2 }} className="absolute -right-2 top-6 rounded-2xl border border-brand-dark/10 bg-white/90 px-5 py-4 backdrop-blur-sm shadow-lg lg:-right-8">
            <div className="font-serif text-3xl text-brand-green">57</div>
            <div className="text-xs text-brand-slate">lives changed</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MarqueeBand() {
  const words = ["Compassion", "Professionalism", "Safety", "Independence", "Hope", "Trust", "Stability"];
  return (
    <div className="border-y border-brand-dark/10 bg-brand-surface py-6">
      <Marquee speed={40} gradient={false} autoFill>
        {words.map((w) => (
          <span key={w} className="mx-8 inline-flex items-center gap-8 font-serif text-3xl italic text-brand-dark/80 md:text-5xl">
            {w} <span className="not-italic text-brand-leaf">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Chapter({ n, label }) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="font-mono-a text-sm text-brand-green">/{n}</span>
      <span className="text-xs font-semibold uppercase tracking-[0.24em] text-brand-slate">{label}</span>
    </div>
  );
}

function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  return (
    <Section>
      <Chapter n="01" label="Who we are" />
      <div className="mt-10 grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-serif text-4xl leading-[1.08] tracking-tight text-brand-dark md:text-5xl lg:text-6xl">
              Founded by people with lived experience of the refugee journey.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl text-lg text-brand-slate">
              We understand the challenges of starting a new life in the UK — because we have lived them. Shelterease provides safe accommodation alongside practical support with housing, benefits, employment, education & ESOL, budgeting, wellbeing and community integration.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-6 max-w-2xl text-lg text-brand-slate">
              Our aim is simple: to help every resident become independent, confident, and fully integrated into their local community.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-10"><Btn to="/about" variant="outline" testid="manifesto-about-btn">Our Story</Btn></div>
          </Reveal>
        </div>
        <div className="relative lg:col-span-5">
          <div className="overflow-hidden rounded-2xl">
            <motion.img style={{ y }} src={IMG.welcomeUmbrella} alt="Refugees welcomed in the UK" className="h-[34rem] w-full scale-110 object-cover" />
          </div>
        </div>
      </div>
    </Section>
  );
}

function ServicesBento() {
  return (
    <Section className="bg-brand-surface/60">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Chapter n="02" label="Support services" />
          <Reveal><h2 className="mt-6 font-serif text-4xl tracking-tight text-brand-dark md:text-5xl">Support for every step<br />of the journey.</h2></Reveal>
        </div>
        <Reveal delay={0.1}><Btn to="/support-services" variant="outline" testid="services-all-btn">All services</Btn></Reveal>
      </div>
      <Stagger className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {SERVICES.slice(0, 6).map((s, i) => (
          <IconTile key={s.title} icon={s.icon} title={s.title} desc={s.desc} index={String(i + 1).padStart(2, "0")} />
        ))}
      </Stagger>
    </Section>
  );
}

function WhyChoose() {
  const points = [
    { t: "Lived experience", d: "Support shaped by people who understand the journey first-hand." },
    { t: "Personalised plans", d: "Every resident receives a tailored support plan, not a template." },
    { t: "Trusted by authorities", d: "A professional, reliable partner for local authorities and providers." },
    { t: "Rooted in Coventry", d: "Deep local networks that turn a new city into a genuine home." },
  ];
  return (
    <Section>
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Chapter n="03" label="Why Shelterease" />
          <Reveal><h2 className="mt-6 font-serif text-4xl tracking-tight text-brand-dark md:text-5xl">More than a roof. A route to independence.</h2></Reveal>
          <Reveal delay={0.1}><div className="mt-8 overflow-hidden rounded-2xl"><img src={IMG.cvClinic} alt="CV clinic — employment support session" className="h-72 w-full object-cover" /></div></Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Stagger className="divide-y divide-brand-dark/10">
            {points.map((p, i) => (
              <StaggerItem key={p.t} className="flex gap-6 py-7">
                <span className="font-mono-a text-sm text-brand-green">0{i + 1}</span>
                <div>
                  <h3 className="font-serif text-2xl text-brand-dark">{p.t}</h3>
                  <p className="mt-2 text-brand-slate">{p.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </Section>
  );
}

function ImpactStories() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, []);
  const t = TESTIMONIALS[i];
  return (
    <Section dark>
      <Chapter n="04" label="Our impact" />
      <div className="mt-12 grid grid-cols-2 gap-10 md:grid-cols-4">
        {STATS.map((s) => <StatCounter key={s.label} {...s} />)}
      </div>
      <div className="mt-24 grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-7">
          <Quote className="h-12 w-12 text-brand-leaf" />
          <AnimatePresence mode="wait">
            <motion.blockquote key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}
              className="mt-6 font-serif text-3xl leading-snug text-brand-main md:text-4xl">
              "{t.quote}"
              <footer className="mt-8 flex items-center gap-4">
                <img src={t.img} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
                <span className="text-base not-italic"><span className="block font-semibold text-brand-leaf">{t.name}</span><span className="text-brand-main/60">{t.role}</span></span>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
          <div className="mt-10 flex gap-2">
            {TESTIMONIALS.map((_, idx) => (
              <button key={idx} data-testid={`testimonial-dot-${idx}`} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-10 bg-brand-leaf" : "w-4 bg-white/25"}`} />
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="overflow-hidden rounded-2xl"><img src={IMG.welcomeRefugees} alt="Welcome Refugees — community support" className="h-full min-h-[20rem] w-full object-cover" /></div>
        </div>
      </div>
    </Section>
  );
}

function LatestNews() {
  const items = [
    { c: "Achievement", t: "50 Residents Secure Full-Time Employment", img: IMG.officeHigh5 },
    { c: "Service Update", t: "Access to New ESOL Conversation Classes", img: IMG.esol },
    { c: "Employment", t: "Employment Support for Residents", img: IMG.employmentSupport },
  ];
  return (
    <Section>
      <div className="flex items-end justify-between">
        <div><Chapter n="05" label="Latest news" /><Reveal><h2 className="mt-6 font-serif text-4xl tracking-tight text-brand-dark md:text-5xl">From the community.</h2></Reveal></div>
        <Reveal delay={0.1}><Btn to="/news" variant="outline" testid="news-all-btn">All news</Btn></Reveal>
      </div>
      <Stagger className="mt-14 grid gap-8 md:grid-cols-3">
        {items.map((n) => (
          <StaggerItem key={n.t}>
            <Link to="/news" className="group block">
              <div className="overflow-hidden rounded-2xl"><img src={n.img} alt={n.t} className="h-60 w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
              <span className="mt-5 inline-block text-xs font-semibold uppercase tracking-wider text-brand-green">{n.c}</span>
              <h3 className="mt-2 font-serif text-2xl text-brand-dark transition-colors group-hover:text-brand-green">{n.t}</h3>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </Section>
  );
}

function Partners() {
  return (
    <Section className="bg-brand-surface/60">
      <Reveal><p className="text-center text-xs font-semibold uppercase tracking-[0.24em] text-brand-slate">Trusted by local authorities & partner organisations</p></Reveal>
      <div className="mt-12 grid grid-cols-2 items-center gap-x-8 gap-y-12 sm:grid-cols-3 md:grid-cols-6">
        {PARTNERS.map((p) => (
          <div key={p.name} className="flex items-center justify-center">
            <img src={p.logo} alt={p.name} title={p.name} className="max-h-16 w-auto max-w-[80%] object-contain opacity-90 transition-opacity duration-300 hover:opacity-100" />
          </div>
        ))}
      </div>
    </Section>
  );
}

function ContactCTA() {
  return (
    <Section>
      <div className="grid items-center gap-10 rounded-3xl bg-brand-green p-10 text-white md:grid-cols-2 md:p-16">
        <div>
          <h2 className="font-serif text-4xl md:text-5xl">Ready to work together?</h2>
          <p className="mt-5 max-w-md text-white/80">Whether you're a referral partner, landlord, volunteer or business — we'd love to hear from you.</p>
        </div>
        <div className="flex flex-wrap gap-4 md:justify-end">
          <Btn to="/contact" variant="light" testid="cta-contact-btn">Contact us</Btn>
          <Link to="/referrals" data-testid="cta-referral-btn" className="group inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-white/10">
            Make a referral <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </Section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <MarqueeBand />
      <Manifesto />
      <ServicesBento />
      <WhyChoose />
      <ImpactStories />
      <LatestNews />
      <Partners />
      <ContactCTA />
    </>
  );
}
